/**
 * Fair Home V2 — keeps a trackpad's inertia away from the pager.
 *
 * A Mac trackpad flick keeps sending wheel events for 1–2 s after the fingers
 * leave the pad, with a jagged decaying tail (… 6 6 10 …, … 32 168 25 …).
 * Swiper pages on any delta that grows once its ~500 ms transition is over, so
 * one flick used to turn two or three sections (swiper#7807, still open).
 *
 * `lethargy-ts` tells a hand from inertia by the stream itself: a pause, a delta
 * that does not drop right after a known hand event, or deltas rising for a few
 * events in a row. Only those events may page; the inertial tail is dropped
 * (`useFairHomeV2OwnPager` reads every wheel event through this gate).
 *
 * A reversed direction always passes — inertia cannot turn around, so a swipe
 * back made during the tail of the previous one is never swallowed.
 */
import { Lethargy } from 'lethargy-ts'

/** Deltas below this are jitter, not a direction (lethargy's own sign threshold). */
const DIRECTION_MIN_DELTA = 10

/** Returns whether a wheel event may reach the pager. Keeps the stream's history. */
export function createFairHomeV2WheelGate(lethargy = new Lethargy()) {
  let lastDirection = 0

  return function passes(event: WheelEvent): boolean {
    // A horizontal swipe belongs to the section carousels, not to the pager.
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return true

    const intentional = lethargy.check(event)
    const direction = Math.abs(event.deltaY) >= DIRECTION_MIN_DELTA ? Math.sign(event.deltaY) : 0
    const reversed = direction !== 0 && lastDirection !== 0 && direction !== lastDirection
    if (direction !== 0) lastDirection = direction

    return intentional || reversed
  }
}
